import arrayMove from "array-move";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { getContextsSafely } from "@examspark/api";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  DragHandle,
  Modal,
  ModalProps,
  Sortable,
  SubmitButtons,
  Truncate,
} from "@examspark/common-ui";
import { IconTrashLine } from "@examspark/common-ui/dist/components/icons";
import { PaddedBody } from "@examspark/common-ui/dist/components/modals/Modal";
import { Resource } from "@examspark/types";

import { ExamEditorContext } from "../../exam-editor/examEditor";

import { SortableItemCard, SortableMeta } from "./ActivityEditorItems";

type ContextListItem = Pick<Resource, "name" | "id"> & {
  questions: Resource[];
};

const ArrangeItemsModal = (props: ModalProps) => {
  const [state, dispatch] = useContext(ExamEditorContext);
  const [orderedContexts, setOrderedContexts] = useState<ContextListItem[]>([]);

  const contexts = useMemo(() => {
    return state.questions.reduce<Record<string, ContextListItem>>(
      (acc, question) => {
        const contexts = getContextsSafely(question.contexts);

        if (!contexts.length) {
          if (!acc["_NONE_"]) {
            acc["_NONE_"] = {
              id: "_NONE_",
              name: "(No Passage)",
              questions: [],
            };
          }
          acc["_NONE_"].questions.push(question);
          return acc;
        }

        const itemPassageIds = contexts
          ?.map((context) => context?.id)
          .join("|");
        if (!itemPassageIds) return acc;

        if (!acc[itemPassageIds]) {
          acc[itemPassageIds] = {
            id: itemPassageIds,
            name: contexts?.map((context) => context?.name).join(", "),
            questions: [],
          };
        }

        acc[itemPassageIds].questions.push(question);
        return acc;
      },
      {},
    );
  }, [state.questions]);

  useEffect(() => {
    setOrderedContexts(Object.values(contexts));
  }, [contexts]);

  const handleSubmit = () => {
    const newQuestions: Resource[] = [];

    for (const ctx of orderedContexts) {
      for (const item of state.questions) {
        if (ctx.questions.includes(item)) {
          newQuestions.push(item);
        }
      }
    }

    dispatch({
      type: "UPDATE",
      data: {
        ...state,
        questions: newQuestions,
      },
    });
  };

  return (
    <Modal
      title="Arrange Items By Passage"
      size="small"
      buttons={
        <SubmitButtons
          submitLabel="Arrange Items"
          cancelLabel="Close"
          onCancel={props.onRequestClose}
          onSubmit={() => {
            handleSubmit();
            props.onRequestClose?.();
          }}
        />
      }
      {...props}
    >
      <PaddedBody>
        <Sortable
          lockAxis="y"
          useDragHandle
          lockToContainerEdges
          onSortEnd={({ oldIndex, newIndex }) => {
            setOrderedContexts(arrayMove(orderedContexts, oldIndex, newIndex));
          }}
        >
          {orderedContexts?.map((context) => (
            <SortableItemCard key={context.id}>
              <Sortable.Handle>
                <DragHandle direction="vertical" />
              </Sortable.Handle>
              <SortableMeta>
                <Truncate>{context.name}</Truncate>
              </SortableMeta>
              <div>
                <Button
                  color="danger"
                  variant="plain"
                  title="Remove Passage from Activity"
                  onClick={async () => {
                    const result = await ConfirmModal.show({
                      children: (
                        <CautionBanner variant="plain">
                          Are you sure you want to remove this passage from your
                          activity?
                        </CautionBanner>
                      ),
                    });
                    if (!result) return;
                    const newQuestions = state.questions.filter(
                      (question) =>
                        !context.questions
                          .map((q) => q.id)
                          .includes(question.id),
                    );

                    dispatch({
                      type: "UPDATE",
                      data: {
                        ...state,
                        questions: newQuestions,
                      },
                    });

                    if (newQuestions.length === 0) {
                      props.onRequestClose?.();
                    }
                  }}
                >
                  <IconTrashLine width="16" />
                </Button>
              </div>
            </SortableItemCard>
          ))}
        </Sortable>
      </PaddedBody>
    </Modal>
  );
};

export default ArrangeItemsModal;
